import React, { useEffect, useMemo, useRef } from 'react'
import {
	useParams,
	BrowserRouter as Router,
	Route,
	useHistory,
	Switch,
	useRouteMatch,
	useLocation,
} from 'react-router-dom'

import styles from './UserController.module.sass'
import { useFetchList } from 'store'

import Tabs from '@jimmyboys/liita-ui/dist/components/Tabs'
import Avatar from '@jimmyboys/liita-ui/dist/components/Avatar'
import User from 'models/User'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import RouteTransition from '@jimmyboys/liita-ui/dist/components/RouteTransition'
import useSession from 'hooks/useSession'
import ReactMarkdown from 'react-markdown'
import { useReload } from 'store'
import { API_URL, fetchOptions } from 'store/fetchUtils'
import UserExclusionRulesController from './UserExclusionRulesController'
import UserEventsSubscriptionController from './UserEventsSubscriptionController'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'

const tabs = (match, i18n, searchParams = '', isCurrentUser) => {
	const userTabs = [
		{
			to: `${match.url}/user-exclusion${searchParams}`,
			name: i18n`Allocation rules`,
		},
	]
	if (isCurrentUser) {
		userTabs.push({
			to: `${match.url}/events-subscriptions${searchParams}`,
			name: i18n`Events subscriptions`,
		})
	}
	return userTabs
}

const UserController = () => {
	const { showDialog, i18n, expireSession, pushNotification } = useGlobal()
	const params = useParams()
	const { user: sessionUser } = useSession()
	const reload = useReload()
	const userDataUri = `/users/${params.userId}`
	const innerRouterRef = useRef(null)
	const history = useHistory()
	const match = useRouteMatch()
	const location = useLocation()
	const [, searchParams] = useQueryParams(location)

	const isAdmin = sessionUser?.roles?.some(
		(role) => role.toLowerCase() === 'admin'
	)

	const isCurrentUser = sessionUser?.id && sessionUser.id === params.userId

	const requests = (sessionUser?.roles || []).includes('admin')
		? [userDataUri, `/metadata/user_tags`]
		: [userDataUri]

	const {
		contents: [userData],
	} = useFetchList(requests, [User.fromJSON])

	const user = userData || {}

	useEffect(() => {
		const unListen = history.listen((location) => {
			if (innerRouterRef.current) {
				innerRouterRef.current.history.replace(location)
			}
		})
		return () => {
			unListen && unListen()
		}
	}, [history])

	const subSectionTabs = tabs(match, i18n, searchParams, isCurrentUser)

	const selectedTab = useMemo(() => {
		return subSectionTabs.find(({ to }) => to === location.pathname)?.to
	}, [location, subSectionTabs])

	return (
		<Widget paddings={['small', 'normal', '']}>
			<Widget
				paddings={['small', 'small']}
				className={styles['header']}
				borderRadius={true}
			>
				<HStack justify='space-between'>
					<HStack className={styles['details-container']} gap='small'>
						<span className={styles['avatar-container']}>
							<Avatar
								rounded
								className={styles['avatar']}
								user={user.avatar}
							/>
						</span>
						<div className={styles['user-info']}>
							<h3>{user.name}</h3>
							<HStack gap='xsmall' justify='space-between'>
								{(user.roles || []).length ? (
									(user.roles || []).map(
										({ name: role }, idx) => {
											return (
												<span
													key={idx}
													className={
														styles['user-role']
													}
												>
													{role}
												</span>
											)
										}
									)
								) : (
									<ReactMarkdown
										className={styles['message']}
										source={i18n`There are no roles`}
									/>
								)}
							</HStack>
						</div>
					</HStack>
					<div className={styles['actions']}>
						{isAdmin && !user.unavailability?.willBeUnavailable && (
							<ActionButton
								danger={true}
								onClick={(e) => {
									showDialog(
										'DisableUser',
										i18n`Disable User ${user?.name}`,
										{
											user,
										},
										false,
										undefined,
										'center',
										'disable-user'
									)
										.then(() => reload([userDataUri]))
										.catch(() => null)
								}}
							>
								<small>{i18n`Disable User`}</small>
							</ActionButton>
						)}
						{isAdmin && user.unavailability?.willBeUnavailable && (
							<ActionButton
								onClick={(e) => {
									showDialog(
										'ConfirmDialog',
										i18n`Activate User`,
										{
											user,
											label: i18n`Are you sure you want to activate ${user?.name} account?`,
										},
										false,
										undefined,
										'center',
										'activate-user'
									)
										.then(() => {
											const url = `${API_URL}/api/users/${user?.id}/available`

											fetch(
												url,
												fetchOptions({
													method: 'PUT',
												})
											)
												.then(() => {
													reload([userDataUri])
													pushNotification(
														undefined,
														'success',
														i18n`User enabled successfully!`
													)
												})
												.catch((err) => {
													pushNotification(
														undefined,
														'error',
														err.message
													)
													if (
														err.status === '401' ||
														err.status === 401
													) {
														expireSession()
													}
												})
										})
										.catch(() => null)
								}}
							>
								<small>{i18n`Activate User`}</small>
							</ActionButton>
						)}
						{sessionUser?.id === params.userId && (
							<ActionButton
								onClick={(e) => {
									showDialog(
										'ResetPasswordDialog',
										i18n`Reset Password`,
										{},
										false,
										undefined,
										'center',
										'reset-password'
									)
										.then(() => reload())
										.catch(() => null)
								}}
							>
								<small>{i18n`Reset Password`}</small>
							</ActionButton>
						)}
					</div>
				</HStack>
			</Widget>
			<Widget paddings={['small', '']}>
				<Tabs
					selected={selectedTab}
					justify='flex-start'
					className={styles['section-tabs']}
					tabs={subSectionTabs}
				/>
			</Widget>
			<Widget>
				<Router ref={innerRouterRef}>
					<RouteTransition>
						<Switch>
							{isCurrentUser ? (
								<Route
									path={`${match.path}/events-subscriptions`}
									exact
								>
									<UserEventsSubscriptionController />
								</Route>
							) : null}
							<Route
								path={`${match.path}/:ruleType(user-exclusion)`}
								exact
							>
								<UserExclusionRulesController />
							</Route>
							<Route>
								<Widget
									applyMaxWidth
									paddings={['normal', 'normal']}
								>
									<h1>404</h1>
								</Widget>
							</Route>
						</Switch>
					</RouteTransition>
				</Router>
			</Widget>
		</Widget>
	)
}

export default UserController
